/**
 * Versioned persistence envelopes for the stored JSON shapes.
 *
 * Every JSON blob the app persists is wrapped `{ v, data }` so a future
 * shape change can migrate instead of corrupting or silently dropping.
 * Bare legacy values (pre-envelope writes, and the old `mi.*` keys) are
 * treated as v0 and fed through the caller's `migrateLegacy`.
 *
 * The two single-token enum keys (emlx:theme, emlx:locale) deliberately
 * stay bare validated strings: an envelope adds nothing to a one-word
 * value whose only failure mode is "not one of the allowed words".
 *
 * Reads are non-destructive except for a successful legacy-key
 * migration, which removes the old key after the new one is written so
 * the storage sweep ("erase everything") cannot miss strays.
 *
 * Writes REPORT. `writeEnvelope` used to swallow every failure behind
 * "the in-memory state stays authoritative", which is true of a
 * preference and false of a bookmark: the whole point of a bookmark is
 * the state that is no longer in memory. It now classifies through
 * `utils/storageError` and returns whether the bytes landed, and it
 * forwards to the toast bridge BY DEFAULT so a new call site is loud
 * without having to remember to be. A caller with a better-aimed
 * sentence of its own passes `report: false` and says it itself.
 *
 * Unlike `safePersist`, which only forwards quota and access-denied,
 * this forwards every classified kind. `Toast.tsx` carries copy for all
 * five, and the distinction safePersist draws is about which failures a
 * preference is worth interrupting the reader for. A durable write is
 * worth interrupting the reader for whatever the reason was.
 */

import { errorLog } from "../utils/errorLog";
import {
  classifyStorageError,
  emitStorageToast,
  type StorageWriteResult,
} from "../utils/storageError";

export type { StorageWriteResult };

export interface WriteEnvelopeOptions {
  /**
   * Forward a classified failure to the storage toast bridge. Default
   * true. Pass false when the call site answers the reader itself, so
   * the same failure is not announced twice.
   */
  report?: boolean;
}

export interface ReadEnvelopeOptions<T> {
  /** Current envelope version this caller writes. */
  version: number;
  /** Validates the unwrapped data (post-migration). */
  validate: (data: unknown) => data is T;
  /**
   * Turns a bare legacy value (v0: an unenveloped JSON value from an old
   * write or an old key) into the current shape, or null to discard.
   * Defaults to validating the bare value as-is.
   */
  migrateLegacy?: (bare: unknown) => T | null;
  /** Old keys to migrate from when the primary key is empty. */
  legacyKeys?: string[];
}

function parseRaw(raw: string | null): unknown {
  if (raw == null) return undefined;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

function unwrap<T>(value: unknown, opts: ReadEnvelopeOptions<T>): T | null {
  if (value === undefined) return null;
  const migrate =
    opts.migrateLegacy ?? ((bare: unknown) => (opts.validate(bare) ? bare : null));
  if (
    typeof value === "object" &&
    value !== null &&
    "v" in value &&
    "data" in value &&
    typeof (value as { v: unknown }).v === "number"
  ) {
    const data = (value as { data: unknown }).data;
    return opts.validate(data) ? data : null;
  }
  // Bare value: v0 legacy shape.
  return migrate(value);
}

export function readEnvelope<T>(
  storage: Storage | undefined,
  key: string,
  opts: ReadEnvelopeOptions<T>,
): T | null {
  if (!storage) return null;
  try {
    const primary = unwrap(parseRaw(storage.getItem(key)), opts);
    if (primary !== null) return primary;
    for (const legacyKey of opts.legacyKeys ?? []) {
      const migrated = unwrap(parseRaw(storage.getItem(legacyKey)), opts);
      if (migrated !== null) {
        // Drop the stray ONLY once its replacement is on disk. Removing it
        // unconditionally, as this did before writeEnvelope reported, threw
        // the value off the device entirely whenever the write was refused:
        // the new key was never written and the old one was deleted anyway.
        // Keeping it costs one retry on the next load and loses nothing, so
        // this failure needs no sentence and does not raise one.
        const written = writeEnvelope(storage, key, opts.version, migrated, {
          report: false,
        });
        if (written.ok) {
          try {
            storage.removeItem(legacyKey);
          } catch {
            /* removal is best-effort */
          }
        }
        return migrated;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Write one enveloped value. Returns `{ ok: true }` only when the bytes
 * reached the storage.
 *
 * The two ways to fail are deliberately told apart, the same way
 * `safePersist` tells them apart. `{ ok: false }` with NO `error` means
 * this browser stores nothing for this site at all (private mode,
 * blocked site data, SSR): there is no failure to report, because
 * nothing was ever going to be kept. `{ ok: false, error }` means the
 * storage was there and refused, which is the case worth a sentence.
 */
export function writeEnvelope<T>(
  storage: Storage | undefined,
  key: string,
  version: number,
  data: T,
  opts: WriteEnvelopeOptions = {},
): StorageWriteResult {
  if (!storage) return { ok: false };
  try {
    storage.setItem(key, JSON.stringify({ v: version, data }));
    return { ok: true };
  } catch (err) {
    const error = classifyStorageError(err);
    errorLog("writeEnvelope", err, { key, kind: error.kind });
    if (opts.report !== false) emitStorageToast(error.kind, key);
    return { ok: false, error };
  }
}
